import { of, from, Observable } from 'rxjs';
import { map, mergeMap, catchError, withLatestFrom, switchMap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { State, getLinkFileListState } from '../reducers';

import * as LinkFileListActions from '../actions/link-file-list.actions';
import { LinkFileListService } from '../link-consume/services/link-file-list.service';
import { LinkPathListApiInput } from '../shared/models';

@Injectable()
export class LinkFileListEffects {
  constructor(
    private actions$: Actions,
    private linkFileList: LinkFileListService,
    private store$: Store<State>,
  ) {}

  public load$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(LinkFileListActions.LINK_FILE_LIST_LOAD),
      switchMap((action: LinkFileListActions.LoadAction) => {
        return from(
          this.linkFileList.getData(
            action.payload.key,
            action.payload.publink_id,
            action.payload.sync_id,
            action.payload.passwordlock,
          ),
        ).pipe(
          map((data) => new LinkFileListActions.SuccessAction(data)),
          catchError((err) => of(new LinkFileListActions.ErrorAction(err))),
        );
      }),
    );
  });

  public loadapp$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(LinkFileListActions.LINK_FILE_LIST_LOAD_APP),
      mergeMap((action: LinkFileListActions.LoadAppAction) => {
        return from(
          this.linkFileList.getDataExternal(
            action.payload.publink_id,
            action.payload.sync_id,
            action.payload.passwordlock,
          ),
        ).pipe(
          map((data) => new LinkFileListActions.SuccessAction(data)),
          catchError((err) => of(new LinkFileListActions.ErrorAction(err))),
        );
      }),
    );
  });

  public success$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(LinkFileListActions.LINK_FILE_LIST_SUCCESS),
      withLatestFrom(this.store$.select(getLinkFileListState)),
      map(([action, storeState]) => {
        return new LinkFileListActions.SortAction({
          active: storeState.predicate,
          direction: storeState.direction,
        });
      }),
    );
  });

  public refresh$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(LinkFileListActions.LINK_FILE_LIST_REFRESH),
      withLatestFrom(this.store$.select(getLinkFileListState)),
      map(([action, storeState]) => {
        const input = new LinkPathListApiInput();
        input.key = storeState.linkkey;
        input.sync_id = parseInt(storeState.sync_id, 10);
        input.publink_id = storeState.publink_id;
        input.passwordlock = storeState.passwordlock;

        return new LinkFileListActions.LoadAction(input);
      }),
    );
  });

  public cd$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(LinkFileListActions.LINK_FILE_LIST_CD),
      withLatestFrom(this.store$.select(getLinkFileListState)),
      map(([action, storeState]) => {
        const input = new LinkPathListApiInput();
        input.key = storeState.linkkey;
        input.sync_id = (<LinkFileListActions.ChangeDirAction>action).payload;
        input.publink_id = storeState.publink_id;
        input.passwordlock = storeState.passwordlock;

        return new LinkFileListActions.LoadAction(input);
      }),
    );
  });

  public loadWhiteLabel$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(LinkFileListActions.LINK_FILE_LIST_LOAD_WHITELABEL),
      mergeMap((action: LinkFileListActions.LoadWhiteLabelAction) => {
        return from(this.linkFileList.getWhiteLabel(action.payload)).pipe(
          map((data) => new LinkFileListActions.WhiteLabelSuccessAction(data)),
          catchError((err) => of(new LinkFileListActions.ErrorAction(err))),
        );
      }),
    );
  });
}
